import {
    Calculate as CalculateIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { enqueueSnackbar } from "notistack";
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import {
    useCloseShiftEntry,
    useGetClosingInvoicesData,
    useGetPosEntryDetails,
    useSaveAndSubmit,
    useSearchPOSOpening,
    useValidateLink,
} from "../../shared/hooks/usePOSData";
import { getCurrentDate } from "../../utils/dateUtils";

function toNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function formatPostingDate(d = new Date()) {
    // YYYY-MM-DD
    return d.toISOString().slice(0, 10);
}

function formatPostingTime(d = new Date()) {
    // HH:mm:ss
    return d.toTimeString().slice(0, 8);
}

function buildPaymentReconciliationRows({
    openingEntry,
    invoices,
    prevClosingByMode,
    initializeClosingAmounts,
}) {
    const rowsByMode = new Map();

    // Equivalent to: set_opening_amounts(frm)
    (openingEntry?.balance_details || []).forEach((detail) => {
        const mode = detail?.mode_of_payment;
        if (!mode) return;

        const opening = toNumber(detail?.opening_amount);
        const existing = rowsByMode.get(mode) || {
            mode_of_payment: mode,
            opening_amount: 0,
            expected_amount: 0,
            closing_amount: 0,
            difference: 0,
        };

        existing.opening_amount += opening;
        existing.expected_amount += opening;

        rowsByMode.set(mode, existing);
    });

    // Equivalent to: refresh_payments(d, frm, is_new)
    (invoices || []).forEach((invoice) => {
        const payments = invoice?.payments || [];
        payments.forEach((p) => {
            const mode = p?.mode_of_payment;
            if (!mode) return;

            let amount = toNumber(p?.amount);
            if (
                p?.account &&
                invoice?.account_for_change_amount &&
                p.account === invoice.account_for_change_amount
            ) {
                amount -= toNumber(invoice?.change_amount);
            }

            const existing = rowsByMode.get(mode) || {
                mode_of_payment: mode,
                opening_amount: 0,
                expected_amount: 0,
                closing_amount: 0,
                difference: 0,
            };

            existing.expected_amount += amount;
            rowsByMode.set(mode, existing);
        });
    });

    const rows = Array.from(rowsByMode.values()).sort((a, b) =>
        a.mode_of_payment.localeCompare(b.mode_of_payment)
    );

    // Equivalent to: if (is_new) payment.closing_amount = payment.expected_amount;
    rows.forEach((r) => {
        const prevClosing = prevClosingByMode?.get(r.mode_of_payment);
        const closing = initializeClosingAmounts
            ? r.expected_amount
            : prevClosing ?? r.expected_amount;

        r.closing_amount = toNumber(closing);
        r.difference = toNumber(r.closing_amount) - toNumber(r.expected_amount);
    });

    return rows;
}

function buildTaxesRows(invoices) {
    const rowsByKey = new Map();
    (invoices || []).forEach((invoice) => {
        (invoice?.taxes || []).forEach((t) => {
            const accountHead = t?.account_head;
            const rate = toNumber(t?.rate);
            if (!accountHead) return;

            const key = `${accountHead}::${rate}`;
            const existing = rowsByKey.get(key) || {
                account_head: accountHead,
                rate,
                amount: 0,
            };

            existing.amount += toNumber(t?.tax_amount);
            rowsByKey.set(key, existing);
        });
    });

    return Array.from(rowsByKey.values()).sort((a, b) =>
        a.account_head.localeCompare(b.account_head)
    );
}

function PosCashCloseDialog({
    open,
    onClose,
    onSuccess,
    showExpectedAmounts = true,
}) {
    const { posProfile, userInfo, company, isKitchenUser } =
        useContext(AppContext);
    const { t } = useTranslation();

    const closeShiftEntryMutation = useCloseShiftEntry();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [selectedOpeningEntryName, setSelectedOpeningEntryName] =
        useState(null);
    const [periodEndDate, setPeriodEndDate] = useState(() => getCurrentDate());

    // Equivalent to Desk: link field options come from "check_opening_entry"
    const { data: openingCandidatesRaw, isLoading: loadingOpeningCandidates } =
        useSearchPOSOpening({ user: userInfo?.name });

    const openingCandidates = useMemo(() => {
        const list = openingCandidatesRaw || [];
        return list.filter((o) => {
            // Most backends return at least {name, company, pos_profile, user, period_start_date, status}
            const companyMatch = company?.name
                ? o.company === company.name ||
                  o.company === company.company_name
                : true;
            const posProfileMatch = posProfile?.name
                ? o.pos_profile === posProfile.name ||
                  o.pos_profile === posProfile?.name
                : true;
            return companyMatch && posProfileMatch;
        });
    }, [
        openingCandidatesRaw,
        company?.name,
        company?.company_name,
        posProfile?.name,
    ]);

    useEffect(() => {
        if (!open) return;
        setPeriodEndDate(getCurrentDate());
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (isKitchenUser) return;
        if (!openingCandidates?.length) return;
        if (selectedOpeningEntryName) return;
        if (openingCandidates.length === 1) {
            setSelectedOpeningEntryName(openingCandidates[0]?.name || null);
        }
    }, [open, isKitchenUser, openingCandidates, selectedOpeningEntryName]);

    // Lightweight validate_link (Desk does this when a Link value is set)
    const { data: validatedOpeningLink, isLoading: loadingValidatedLink } =
        useValidateLink("POS Opening Entry", selectedOpeningEntryName, [
            "period_start_date",
            "pos_profile",
            "user",
        ]);

    // Full document load (to get balance_details)
    const { data: selectedOpeningEntry, isLoading: loadingOpeningDoc } =
        useGetPosEntryDetails(selectedOpeningEntryName);

    const closingInvoicesParams = useMemo(
        () => ({
            startDate:
                validatedOpeningLink?.period_start_date ||
                selectedOpeningEntry?.period_start_date,
            endDate: periodEndDate,
            posProfile: posProfile?.name,
            user: userInfo?.name,
        }),
        [
            validatedOpeningLink?.period_start_date,
            selectedOpeningEntry?.period_start_date,
            periodEndDate,
            posProfile?.name,
            userInfo?.name,
        ]
    );

    const { data: posProfileClosingEntries, isLoading: loadingInvoices } =
        useGetClosingInvoicesData(
            closingInvoicesParams.startDate,
            closingInvoicesParams.endDate,
            closingInvoicesParams.posProfile,
            closingInvoicesParams.user,
            undefined, // onSuccess
            undefined // onError
        );

    const saveAndSubmitMutation = useSaveAndSubmit();

    const totals = useMemo(() => {
        if (!posProfileClosingEntries)
            return { grandTotal: 0, netTotal: 0, totalQuantity: 0 };

        let grandTotal = 0;
        let netTotal = 0;
        let totalQuantity = 0;

        posProfileClosingEntries.forEach((invoice) => {
            grandTotal += invoice.grand_total || 0;
            netTotal += invoice.net_total || 0;
            totalQuantity += invoice.total_qty || 0;
        });

        return { grandTotal, netTotal, totalQuantity };
    }, [posProfileClosingEntries]);

    const [paymentReconciliation, setPaymentReconciliation] = useState([]);

    // Equivalent to: reset_values(frm) + set_form_data(data, frm)
    useEffect(() => {
        if (!open) return;
        if (!selectedOpeningEntry) return;
        // Wait until opening entry includes balance_details; otherwise we may build rows without opening_amount
        if (!Array.isArray(selectedOpeningEntry?.balance_details)) return;

        const prevClosingByMode = new Map(
            (paymentReconciliation || []).map((r) => [
                r.mode_of_payment,
                r.closing_amount,
            ])
        );

        const initializeClosingAmounts = paymentReconciliation.length === 0;

        const rows = buildPaymentReconciliationRows({
            openingEntry: selectedOpeningEntry,
            invoices: posProfileClosingEntries || [],
            prevClosingByMode,
            initializeClosingAmounts,
        });

        setPaymentReconciliation(rows);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open,
        selectedOpeningEntry?.name,
        selectedOpeningEntry?.balance_details?.length,
        posProfileClosingEntries,
    ]);

    const taxes = useMemo(() => {
        return buildTaxesRows(posProfileClosingEntries || []);
    }, [posProfileClosingEntries]);

    const posTransactions = useMemo(() => {
        return (posProfileClosingEntries || []).map((d) => ({
            pos_invoice: d?.name,
            posting_date: d?.posting_date,
            grand_total: d?.grand_total,
            customer: d?.customer,
        }));
    }, [posProfileClosingEntries]);

    // Generate unique document name
    const generateDocName = () => {
        return `new-pos-closing-entry-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
    };

    const handleClosingAmountChange = useCallback((mode, value) => {
        setPaymentReconciliation((prev) =>
            (prev || []).map((r) => {
                if (r.mode_of_payment !== mode) return r;
                const closing = toNumber(value);
                const expected = toNumber(r.expected_amount);
                return {
                    ...r,
                    closing_amount: closing,
                    difference: closing - expected,
                };
            })
        );
    }, []);

    const hasDifferences = useMemo(() => {
        return (paymentReconciliation || []).some(
            (r) => Math.abs(toNumber(r.difference)) > 0.01
        );
    }, [paymentReconciliation]);

    const handleSubmit = async () => {
        if (!selectedOpeningEntry || !posProfileClosingEntries?.length) return;

        setIsSubmitting(true);
        try {
            // Equivalent to: before_save(frm) recomputation
            const prevClosingByMode = new Map(
                (paymentReconciliation || []).map((r) => [
                    r.mode_of_payment,
                    r.closing_amount,
                ])
            );

            const rowsFresh = buildPaymentReconciliationRows({
                openingEntry: selectedOpeningEntry,
                invoices: posProfileClosingEntries || [],
                prevClosingByMode,
                initializeClosingAmounts: false,
            });

            const taxesFresh = buildTaxesRows(posProfileClosingEntries || []);

            const entryName = generateDocName();
            const now = new Date();

            const cashRow =
                rowsFresh.find((r) => r.mode_of_payment === "Cash") || null;

            // Write-off amount should reflect cash difference between expected and actual cash:
            // expected - actual (so shortage is positive)
            const writeOffAmount = cashRow
                ? toNumber(cashRow.expected_amount) -
                  toNumber(cashRow.closing_amount)
                : 0;

            const writeOffAccount =
                posProfile?.write_off_account || company?.write_off_account;

            const closingEntryDoc = {
                docstatus: 0,
                doctype: "POS Closing Entry",
                name: entryName,
                __islocal: 1,
                __unsaved: 1,
                owner: userInfo?.name,
                period_start_date:
                    validatedOpeningLink?.period_start_date ||
                    selectedOpeningEntry?.period_start_date,
                period_end_date: periodEndDate,
                posting_date: formatPostingDate(now),
                posting_time: formatPostingTime(now),
                status: "Draft",
                company: company?.name || posProfile?.company,
                pos_profile:
                    validatedOpeningLink?.pos_profile || posProfile?.name,
                user: validatedOpeningLink?.user || userInfo?.name,
                pos_opening_entry: selectedOpeningEntry?.name,
                grand_total: toNumber(totals.grandTotal),
                net_total: toNumber(totals.netTotal),
                total_quantity: toNumber(totals.totalQuantity),
                // Align with Desk behavior: store cash difference in write-off fields
                write_off_account: writeOffAccount || undefined,
                write_off_cost_center:
                    company?.cost_center ||
                    posProfile?.cost_center ||
                    undefined,
                write_off_amount: writeOffAmount,
                pos_transactions: (posProfileClosingEntries || []).map(
                    (invoice, idx) => ({
                        docstatus: 0,
                        doctype: "POS Invoice Reference",
                        name: `new-pos-invoice-reference-${idx + 1}`,
                        __islocal: 1,
                        __unsaved: 1,
                        owner: invoice?.owner || userInfo?.name,
                        parent: entryName,
                        parentfield: "pos_transactions",
                        parenttype: "POS Closing Entry",
                        idx: idx + 1,
                        pos_invoice: invoice?.name,
                        posting_date: formatPostingDate(now),
                        grand_total: invoice?.grand_total,
                        customer: invoice?.customer,
                        is_return: invoice?.is_return,
                        __checked: 1,
                    })
                ),
                payment_reconciliation: rowsFresh.map((row, idx) => ({
                    docstatus: 0,
                    doctype: "POS Closing Entry Detail",
                    name: `new-pos-closing-entry-detail-${idx + 1}`,
                    __islocal: 1,
                    __unsaved: 1,
                    owner: userInfo?.name,
                    parent: entryName,
                    parentfield: "payment_reconciliation",
                    parenttype: "POS Closing Entry",
                    idx: idx + 1,
                    mode_of_payment: row.mode_of_payment,
                    opening_amount: toNumber(row.opening_amount),
                    expected_amount: toNumber(row.expected_amount),
                    closing_amount: toNumber(row.closing_amount),
                    difference: toNumber(row.difference),
                })),
                // Keep the same field name as the Desk form; server will validate child doctype.
                taxes: taxesFresh.map((t, idx) => ({
                    account_head: t.account_head,
                    rate: toNumber(t.rate),
                    amount: toNumber(t.amount),
                    idx: idx + 1,
                })),
            };

            closeShiftEntryMutation.mutate(
                {
                    amount_in_register: paymentReconciliation,
                    pos_opening_entry: selectedOpeningEntry?.name,
                },
                {
                    onSuccess: (data) => {
                        console.log("data", data);
                        enqueueSnackbar(t("posCashClose.submitted"), {
                            variant: "success",
                        });
                        if (onSuccess) onSuccess();
                        handleClose();
                        setIsSubmitting(false);
                    },
                    // enqueueSnackbar("POS Closing Entry submitted", {
                    //     variant: "success",
                    // });
                    // if (onSuccess) onSuccess();
                    // handleClose();
                    // setIsSubmitting(false);
                }
            );

            // await new Promise((resolve, reject) => {
            //     saveAndSubmitMutation.mutate(
            //         { doc: closingEntryDoc },
            //         {
            //             onSuccess: resolve,
            //             onError: reject,
            //         }
            //     );
            // });

            // enqueueSnackbar("POS Closing Entry submitted", {
            //     variant: "success",
            // });
            // if (onSuccess) onSuccess();
            // handleClose();
            // setIsSubmitting(false);
        } catch (error) {
            console.error("Error creating POS Closing Entry:", error);
            enqueueSnackbar(t("posCashClose.submitError"), {
                variant: "error",
            });
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setPaymentReconciliation([]);
        if (onClose) onClose();
    };

    if (
        loadingInvoices ||
        loadingOpeningCandidates ||
        loadingValidatedLink ||
        loadingOpeningDoc
    ) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogContent>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={4}
                    >
                        <CircularProgress />
                        <Typography variant="h6" sx={{ ml: 2 }}>
                            {t("common.loading")}
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            {/* Main Cash Close Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h5" fontWeight="bold">
                            {t("posCashClose.title")}
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={3}>
                        {/* POS Opening Entry */}
                        <Grid item xs={12}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                    >
                                        {t("posCashClose.openingEntryTitle")}
                                    </Typography>
                                    {openingCandidates.length > 1 ? (
                                        <Autocomplete
                                            options={openingCandidates || []}
                                            getOptionLabel={(o) =>
                                                o?.name
                                                    ? `${o.name}${
                                                          o.period_start_date
                                                              ? ` • ${o.period_start_date}`
                                                              : ""
                                                      }`
                                                    : ""
                                            }
                                            value={
                                                openingCandidates.find(
                                                    (o) =>
                                                        o.name ===
                                                        selectedOpeningEntryName
                                                ) || null
                                            }
                                            onChange={(_, v) => {
                                                setPaymentReconciliation([]);
                                                setSelectedOpeningEntryName(
                                                    v?.name || null
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t(
                                                        "posCashClose.selectOpeningEntry"
                                                    )}
                                                    size="small"
                                                />
                                            )}
                                        />
                                    ) : openingCandidates.length === 1 ? (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={t(
                                                "posCashClose.openingEntry"
                                            )}
                                            value={
                                                openingCandidates[0]?.name || ""
                                            }
                                            disabled
                                        />
                                    ) : (
                                        <Alert severity="warning">
                                            {t("posCashClose.noOpeningEntry")}
                                        </Alert>
                                    )}

                                    {(validatedOpeningLink?.period_start_date ||
                                        selectedOpeningEntry?.period_start_date) && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            display="block"
                                            sx={{ mt: 1 }}
                                        >
                                            {t("posCashClose.periodStart")}:{" "}
                                            {validatedOpeningLink?.period_start_date ||
                                                selectedOpeningEntry?.period_start_date}
                                            {" • "}
                                            {t("posCashClose.periodEnd")}:{" "}
                                            {periodEndDate}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Summary Section */}
                        <Grid item xs={12}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                    >
                                        {t("posCashClose.transactionSummary")}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box textAlign="center">
                                                <Typography
                                                    variant="h4"
                                                    color="success.main"
                                                >
                                                    {posProfileClosingEntries?.length ||
                                                        0}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {t("posCashClose.invoices")}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {showExpectedAmounts && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Box textAlign="center">
                                                    <Typography
                                                        variant="h4"
                                                        color="primary"
                                                    >
                                                        ₪
                                                        {totals.grandTotal.toFixed(
                                                            2
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {t("posCashClose.totalSales")}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box textAlign="center">
                                                <Typography
                                                    variant="h4"
                                                    color="info.main"
                                                >
                                                    {totals.totalQuantity}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {t("posCashClose.totalItems")}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Reconciliation Table */}
                        {showExpectedAmounts && (
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="primary"
                                        >
                                            {t("posCashClose.paymentReconciliation")}
                                        </Typography>
                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                        >
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            <strong>
                                                                {t("posCashClose.paymentMethod")}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t("posCashClose.openingAmount")}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t("posCashClose.expectedAmount")}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t("posCashClose.actualAmount")}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t("posCashClose.differenceAmount")}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <strong>
                                                                {t("posCashClose.status")}
                                                            </strong>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(
                                                        paymentReconciliation ||
                                                        []
                                                    ).map((row) => {
                                                        const isBalanced =
                                                            Math.abs(
                                                                toNumber(
                                                                    row.difference
                                                                )
                                                            ) < 0.01;
                                                        return (
                                                            <TableRow
                                                                key={
                                                                    row.mode_of_payment
                                                                }
                                                            >
                                                                <TableCell>
                                                                    {
                                                                        row.mode_of_payment
                                                                    }
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {toNumber(
                                                                        row.opening_amount
                                                                    ).toFixed(
                                                                        2
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {toNumber(
                                                                        row.expected_amount
                                                                    ).toFixed(
                                                                        2
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <TextField
                                                                        type="number"
                                                                        size="small"
                                                                        value={
                                                                            row.closing_amount
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleClosingAmountChange(
                                                                                row.mode_of_payment,
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                        inputProps={{
                                                                            style: {
                                                                                textAlign:
                                                                                    "right",
                                                                            },
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    sx={{
                                                                        color:
                                                                            row.difference >
                                                                            0
                                                                                ? "success.main"
                                                                                : row.difference <
                                                                                  0
                                                                                ? "error.main"
                                                                                : "text.primary",
                                                                    }}
                                                                >
                                                                    {row.difference >
                                                                    0
                                                                        ? "+"
                                                                        : ""}
                                                                    {toNumber(
                                                                        row.difference
                                                                    ).toFixed(
                                                                        2
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Chip
                                                                        label={
                                                                            isBalanced
                                                                                ? t(
                                                                                      "posCashClose.balanced"
                                                                                  )
                                                                                : t(
                                                                                      "posCashClose.difference"
                                                                                  )
                                                                        }
                                                                        color={
                                                                            isBalanced
                                                                                ? "success"
                                                                                : "warning"
                                                                        }
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Taxes */}
                        {showExpectedAmounts && taxes.length > 0 && (
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="primary"
                                        >
                                            {t("posCashClose.taxes")}
                                        </Typography>
                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                        >
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            <strong>
                                                                {t("posCashClose.accountHead")}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t("posCashClose.rate")}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t("posCashClose.taxAmount")}
                                                            </strong>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {taxes.map((t) => (
                                                        <TableRow
                                                            key={`${t.account_head}:${t.rate}`}
                                                        >
                                                            <TableCell>
                                                                {t.account_head}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {toNumber(
                                                                    t.rate
                                                                ).toFixed(2)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {toNumber(
                                                                    t.amount
                                                                ).toFixed(2)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Warnings */}
                        {showExpectedAmounts && hasDifferences && (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    {t("posCashClose.differencesWarning")}
                                </Alert>
                            </Grid>
                        )}

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap={2}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    {t("common.cancel")}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={
                                        isSubmitting ||
                                        !posProfileClosingEntries?.length
                                    }
                                    startIcon={
                                        isSubmitting ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <CalculateIcon />
                                        )
                                    }
                                    sx={{ minWidth: 140 }}
                                >
                                    {isSubmitting
                                        ? t("posCashClose.closing")
                                        : t("posCashClose.submitClosing")}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default PosCashCloseDialog;
